import React, { useContext, useEffect, useState } from "react";
import _, { set } from 'lodash';
import cx from 'classnames';

import { CollectionAsset, CollectionEditState } from "src/types/collections";
import { MenuState, SectionState } from "../../types";
import useMeasure from "react-use-measure";
import { animated, useChain, useSpringRef, useTransition } from "@react-spring/web";
import { BannerView } from "src/components/banner";
import { BannerEditor } from "../../components/banner/library";
import { BannerFilters } from "../../components/banner/filter";
import { CropEditor } from "../../components/crop";
import { CollectionContent } from "../../components/content";
import { DraggingSectionCard, SectionBar } from "./sections";
import { CarouselLayout } from "./layout";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { DraggingAssetCard } from "./card";
import { fixCursorSnapOffset } from "src/services/dnd";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { CollectionDescription } from "../../components/description";

import styles from './carousel.module.scss';
import { CustomerLogo } from "../../components/customerLogo";
import { CollectionName } from "../../components/name";
import { CollectionContext } from "../../components/context";

interface CarouselExperienceProps {
  collection: CollectionEditState;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

interface DraggingAssetState {
  asset: CollectionAsset;
  overSection: boolean;
}

const CarouselExperience: React.FC<CarouselExperienceProps> = ({ collection, menuState, onMenuChange }) => {
  const collectionContext = useContext(CollectionContext);

  const showAssets = menuState.primaryAction === 'view' || menuState.primaryAction === 'edit';
  const showBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === null;
  const cropBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'crop';
  const showFilters = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'filter';

  const [bodyRef, bodyBounds] = useMeasure();
  const [descriptionRef, descriptionBounds] = useMeasure();
  const [headerHeight, setHeaderHeight] = useState('auto');
  const [descriptionHeight, setDescriptionHeight] = useState('none');

  const banner = _.get(collection, 'banner');
  const customerLogo = _.get(collection, 'customerLogo');
  const [draggingAsset, setDraggingAsset] = useState<DraggingAssetState | null>(null);
  const [draggingSection, setDraggingSection] = useState<SectionState | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const assetsRef = useSpringRef();
  const assetsTransitions = useTransition(showAssets, {
    ref: assetsRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
  });

  const bannerEditorRef = useSpringRef();
  const bannerEditorTransitions = useTransition(showBanner, {
    ref: bannerEditorRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const bannerFilterRef = useSpringRef();
  const bannerFilterTransitions = useTransition(showFilters, {
    ref: bannerFilterRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const cropperRef = useSpringRef();
  const cropperTransitions = useTransition(cropBanner, {
    ref: cropperRef,
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: showBanner ? 0 : 0,
    config: {
      friction: 14,
      tension: 120,
    },
  });

  useChain([assetsRef, bannerEditorRef, bannerFilterRef, cropperRef], [0, 0, 0, 0]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      calculateHeaderDimensions();
    });
  }, [bodyBounds, customerLogo, menuState.primaryAction]);

  const handleAssetDragStart = (event) => {
    const asset = event.active.data.current.asset;
    const section = event.active.data.current.section;

    if (asset) {
      setDraggingAsset({
        asset: asset,
        overSection: false
      });
    } else if (section) {
      setDraggingSection(section);
    }
  }

  const handleAssetDragMove = (event) => {
    const entityType = event.over?.data.current?.type;
    setDraggingAsset((prev) => {
      if (!prev) {
        return null;
      }

      return {
        ...prev,
        overSection: entityType === 'section'
      };
    });
  }

  const handleAssetDragEnd = () => {
    setDraggingAsset(null);
    setDraggingSection(null);
  }

  const calculateHeaderDimensions = () => {
    const editing = menuState.primaryAction !== 'view';
    const element = document.querySelector(`.${styles.layout}`) as HTMLElement;
    const bodyElement = document.querySelector(`.${styles.body}`) as HTMLElement;
    const footerOffset = 46;
    const logoMaxHeight = 40;
    const sectionHeight = 40;

    if (!element || !bodyElement) {
      return;
    }

    const conversation = collectionContext.entityType === 'conversation';
    let height = element.clientHeight - bodyElement.offsetHeight - footerOffset - (editing? 150 : 110);
    if(conversation){
      height = height + 60;
    }

    if(customerLogo) {
      height = height + logoMaxHeight;
    }

    if((collection.sectionsCount ?? 0) > 0){
      height = height + sectionHeight;
    }

    setHeaderHeight(height + 'px');

    let descriptionHeight = height;
    if (customerLogo) {
      descriptionHeight = descriptionHeight - 140;
    }

    setDescriptionHeight((descriptionHeight) + 'px');
  }

  return (
    <div className={styles.layout}>
      <div className={styles.background}>
        <BannerView banner={banner} defaultBanner="/images/collections/background.jpeg" />
      </div>
      <div className={cx(styles.inner, styles[collectionContext.entityType], {
        [styles.editing]: menuState.primaryAction === 'edit',
      })}>
        {assetsTransitions((style, item) => (
          item && <animated.div className={cx(styles.header)} style={{ ...style, maxHeight: headerHeight }}>
            {menuState.primaryAction === 'edit' && collectionContext.entityType === 'conversation' &&
              <CollectionName collection={collection} className={styles.collectionName} />
            }

            <CustomerLogo className={styles.customerLogo}
              collection={collection}
              menuState={menuState}
              onMenuChange={onMenuChange}
            />
            <div ref={descriptionRef} className={styles.description}>
              <CollectionDescription
                collection={collection}
                menuState={menuState}
                maxHeight={descriptionHeight}
                onMenuChange={onMenuChange}
              />
            </div>
          </animated.div>
        ))}
        <div ref={bodyRef} className={cx(styles.body, {
          [styles.cropping]: cropBanner
        })}>
          <DndContext sensors={sensors}
            collisionDetection={fixCursorSnapOffset}
            modifiers={[]}
            onDragStart={handleAssetDragStart}
            onDragMove={handleAssetDragMove}
            onDragEnd={handleAssetDragEnd}
          >
            {assetsTransitions((style, item) => (
              item && <animated.div style={style} className={styles.content}>
                <div className={styles.assets}>
                  <CollectionContent
                    collection={collection}
                    sectionBar={SectionBar}
                    layout={CarouselLayout}
                    menuState={menuState}
                    onMenuChange={onMenuChange}
                  />
                </div>
              </animated.div>
            ))}

            {bannerEditorTransitions((style, item) => (
              item && <animated.div style={style} className={cx(styles.content, styles.bannerEditor)}>
                <BannerEditor collection={collection} />
              </animated.div>
            ))}

            {bannerFilterTransitions((style, item) => (
              item && <animated.div style={style} className={cx(styles.content, styles.bannerEditor)}>
                <BannerFilters collection={collection} />
              </animated.div>
            ))}

            {cropperTransitions((style, item) => (
              item && <animated.div style={style} className={styles.content}>
                <CropEditor enabled={cropBanner}
                  collection={collection}
                  menuState={menuState}
                  onMenuChange={onMenuChange}
                />
              </animated.div>
            ))}

            <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null} adjustScale={false}>
              {draggingAsset && (
                <DraggingAssetCard asset={draggingAsset.asset} overSection={draggingAsset.overSection} />
              )}

              {draggingSection && (
                <DraggingSectionCard section={draggingSection} />
              )}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
    </div>
  );
}

export { CarouselExperience };